import { timestampFormatter } from "../../../core/utils/timestampFormatter";

const FIRST_RUN_WINDOW_MS = 5 * 60 * 1000;
/**
 * A sync that still claims to be running this long after it started is not progress, it is
 * stuck state (e.g. a run that found nothing new and never reported completion). Past this
 * point the UI stops spinning and polling for it.
 */
export const SYNC_STALE_AFTER_MS = 30 * 60 * 1000;
/** Connector finished this long ago and the first transform still has not reported done. */
const FIRST_RUN_TRANSFORM_STALE_AFTER_MS = 20 * 60 * 1000;
const SYNC_STALE_PROGRESS_PERCENT = (SYNC_STALE_AFTER_MS / FIRST_RUN_WINDOW_MS) * 100;
const TIMESTAMP_MINUTE_FORMAT = { includeSeconds: false };

/**
 * Normalize API timestamp values (seconds, ms, or ISO strings) to epoch milliseconds.
 *
 * @example
 * parseWorkflowTimestampMs(1715000000);
 * // 1715000000000
 *
 * @example
 * parseWorkflowTimestampMs("2026-05-12T14:00:00Z");
 * // epoch ms for that instant
 */
export const parseWorkflowTimestampMs = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value <= 0) {
      return 0;
    }
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }
  const text = String(value || "").trim();
  if (!text) {
    return 0;
  }
  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseTimestampMs = parseWorkflowTimestampMs;
const FIRST_RUN_RUNNING_STATES = new Set([
  "QUEUED",
  "CONNECTOR_RUNNING",
  "TRANSFORM_RUNNING",
  "RUNNING",
]);

/**
 * True when a "first run in progress" workflow has been in that state implausibly long:
 * past SYNC_STALE_AFTER_MS since it started, or past FIRST_RUN_TRANSFORM_STALE_AFTER_MS since
 * its connector last synced (a transform that never reports completion, e.g. when the sync
 * found nothing new to process).
 *
 * @example
 * isFirstRunStale({ created_at: "2026-10-29T08:00:00Z" }, Date.parse("2026-10-29T09:00:00Z"));
 * // true
 */
export const isFirstRunStale = (workflow, nowMs = Date.now()) => {
  const startedMs =
    parseTimestampMs(workflow?.first_run_started_at) || parseTimestampMs(workflow?.created_at);
  if (startedMs > 0 && nowMs - startedMs > SYNC_STALE_AFTER_MS) return true;
  const syncedMs = parseTimestampMs(workflow?.last_synced_at);
  return (
    syncedMs > 0 &&
    syncedMs >= startedMs &&
    nowMs - syncedMs > FIRST_RUN_TRANSFORM_STALE_AFTER_MS
  );
};

const getFirstRunState = (workflow) =>
  String(workflow?.first_run_state || "").trim().toUpperCase();

const isFirstRunFlagInProgress = (workflow) => workflow?.first_run_in_progress === true;

/**
 * Compute workflow lifecycle status used by status cells.
 *
 * @example
 * // Input
 * getWorkflowLifecycleStatus({ next_run_time: 1760000000000 });
 * // Output
 * { primary: "Next run at 2025-10-09 09:33", secondary: "", showProgress: false, progressPercent: 0 }
 */
export const getWorkflowLifecycleStatus = (workflow) => {
  if (!workflow) {
    return {
      primary: "Setup in progress",
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }
  if (workflow.active === false) {
    return {
      primary: "Disabled",
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }

  const nowMs = Date.now();
  const firstRunState = getFirstRunState(workflow);
  const createdAt = parseTimestampMs(workflow.created_at);
  const lastSyncedAt = parseTimestampMs(workflow.last_synced_at);
  const nextRunAt = parseTimestampMs(workflow.next_run_time);
  const isFirstRunPending = createdAt > 0 && (!lastSyncedAt || lastSyncedAt <= createdAt);
  const isJobRunning = String(workflow.job_status || "").toUpperCase() === "RUNNING";
  const isScheduledRunOverdue =
    nextRunAt > 0 && nowMs >= nextRunAt && (!lastSyncedAt || lastSyncedAt < nextRunAt);

  if (isFirstRunFlagInProgress(workflow) || FIRST_RUN_RUNNING_STATES.has(firstRunState)) {
    const firstRunStartMs =
      parseTimestampMs(workflow.first_run_started_at) || createdAt || nowMs;
    const elapsed = Math.max(0, nowMs - firstRunStartMs);
    return {
      primary: "Workflow first run in progress",
      secondary: "",
      showProgress: true,
      progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
    };
  }

  if (firstRunState === "FAILED") {
    return {
      primary: "First run failed",
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }

  if (isFirstRunPending && nowMs < createdAt + FIRST_RUN_WINDOW_MS) {
    const elapsed = Math.max(0, nowMs - createdAt);
    return {
      primary: "Workflow first run in progress",
      secondary: "",
      showProgress: true,
      progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
    };
  }

  // Once first run is finished, never show setup progress again.
  if (!isFirstRunPending) {
    if (isScheduledRunOverdue) {
      const elapsed = Math.max(0, nowMs - nextRunAt);
      return {
        primary: `Run started at scheduled time (${timestampFormatter(
          nextRunAt,
          TIMESTAMP_MINUTE_FORMAT
        )})`,
        secondary: "Still running beyond scheduled time",
        showProgress: true,
        progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
      };
    }
    if (nextRunAt > 0 && nextRunAt > nowMs) {
      return {
        primary: `Next run at ${timestampFormatter(nextRunAt, TIMESTAMP_MINUTE_FORMAT)}`,
        secondary: "",
        showProgress: false,
        progressPercent: 0,
      };
    }
    if (isJobRunning && lastSyncedAt > 0) {
      const progressStartMs = nextRunAt > 0 ? nextRunAt : lastSyncedAt;
      const elapsed = Math.max(0, nowMs - progressStartMs);
      return {
        primary: `Running (last sync ${timestampFormatter(lastSyncedAt, TIMESTAMP_MINUTE_FORMAT)})`,
        secondary: "Execution in progress",
        showProgress: true,
        progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
      };
    }
    if (lastSyncedAt > 0) {
      return {
        primary: `Last run at ${timestampFormatter(lastSyncedAt, TIMESTAMP_MINUTE_FORMAT)}`,
        secondary: "",
        showProgress: false,
        progressPercent: 0,
      };
    }
    return {
      primary: "Workflow is active. Your next digest will run on schedule.",
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }

  if (isScheduledRunOverdue) {
    const elapsed = Math.max(0, nowMs - nextRunAt);
    return {
      primary: `Run started at scheduled time (${timestampFormatter(
        nextRunAt,
        TIMESTAMP_MINUTE_FORMAT
      )})`,
      secondary: "Still running beyond scheduled time",
      showProgress: true,
      progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
    };
  }

  if (nextRunAt > 0 && nextRunAt > nowMs) {
    return {
      primary: `Next run at ${timestampFormatter(nextRunAt, TIMESTAMP_MINUTE_FORMAT)}`,
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }

  if (isJobRunning && lastSyncedAt > 0) {
    const progressStartMs = nextRunAt > 0 ? nextRunAt : lastSyncedAt;
    const elapsed = Math.max(0, nowMs - progressStartMs);
    return {
      primary: `Running (last sync ${timestampFormatter(lastSyncedAt, TIMESTAMP_MINUTE_FORMAT)})`,
      secondary: "Execution in progress",
      showProgress: true,
      progressPercent: (elapsed / FIRST_RUN_WINDOW_MS) * 100,
    };
  }

  if (lastSyncedAt > 0) {
    return {
      primary: `Last run at ${timestampFormatter(lastSyncedAt, TIMESTAMP_MINUTE_FORMAT)}`,
      secondary: "",
      showProgress: false,
      progressPercent: 0,
    };
  }

  return {
    primary: "Workflow is active. Your next digest will run on schedule.",
    secondary: "",
    showProgress: false,
    progressPercent: 0,
  };
};

/**
 * Convert backend job status to user-friendly copy.
 *
 * @example
 * // Input
 * getJobStatusLabel("RUNNING");
 * // Output
 * "Execution in progress"
 */
export const getJobStatusLabel = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "RUNNING") {
    return "Execution in progress";
  }
  if (normalized === "DONE") {
    return "Last run completed";
  }
  if (normalized === "FAILED") {
    return "Last run failed";
  }
  if (normalized === "INIT" || normalized === "INFERENCE_INIT") {
    return "Scheduled";
  }
  return "";
};

/**
 * True while a connector workflow is still running its first digest or an active job.
 *
 * @example
 * // Input — waiting for first Gmail digest
 * shouldAutoRefreshActionItemsForFirstRun({
 *   created_at: 1715155200000,
 *   last_synced_at: 0,
 *   job_status: "RUNNING",
 * });
 * // Output
 * // true
 *
 * @example
 * // Input — first digest done; backend reports INIT for the next scheduled job
 * shouldAutoRefreshActionItemsForFirstRun({
 *   created_at: 1715155200000,
 *   last_synced_at: 1715159800000,
 *   job_status: "INIT",
 * });
 * // Output
 * // false
 */
const FIRST_RUN_ACTIVE_STATES = new Set([
  "CONNECTOR_RUNNING",
  "TRANSFORM_RUNNING",
  "RUNNING",
]);

/**
 * True when the workflow's first connector+transform cycle has finished.
 *
 * @example
 * // Input
 * isWorkflowFirstRunComplete({
 *   first_run_state: "completed",
 *   first_run_in_progress: false,
 *   job_status: "INIT",
 * });
 * // Output
 * // true
 */
export const isWorkflowFirstRunComplete = (workflow) => {
  if (!workflow) return false;
  const firstRunState = getFirstRunState(workflow);
  if (firstRunState === "COMPLETED" || firstRunState === "FAILED") {
    return true;
  }
  if (parseWorkflowTimestampMs(workflow.first_run_completed_at) > 0) {
    return true;
  }
  return workflow.first_run_in_progress === false;
};

export const shouldAutoRefreshActionItemsForFirstRun = (workflow) => {
  if (!workflow || isWorkflowFirstRunComplete(workflow) || isFirstRunStale(workflow)) {
    return false;
  }
  const firstRunState = getFirstRunState(workflow);
  if (FIRST_RUN_ACTIVE_STATES.has(firstRunState)) {
    return true;
  }
  if (firstRunState === "QUEUED" && workflow.first_run_in_progress === true) {
    return true;
  }
  const normalizedJobStatus = String(workflow.job_status || "").trim().toUpperCase();
  return normalizedJobStatus === "RUNNING" || normalizedJobStatus === "PENDING";
};

/**
 * True while a workflow is still running its first transform (first-run lifecycle only).
 *
 * Never treats the next scheduled INIT job as first-run activity.
 *
 * @example
 * // Input — first transform done, next run scheduled as INIT
 * isWorkflowLikelyGeneratingFirstItems({
 *   first_run_state: "completed",
 *   first_run_in_progress: false,
 *   job_status: "INIT",
 * });
 * // Output
 * // false
 */
export const isWorkflowLikelyGeneratingFirstItems = (workflow) => {
  if (!workflow || isWorkflowFirstRunComplete(workflow) || isFirstRunStale(workflow)) {
    return false;
  }
  const firstRunState = getFirstRunState(workflow);
  if (FIRST_RUN_ACTIVE_STATES.has(firstRunState)) {
    return true;
  }
  return firstRunState === "QUEUED" && workflow.first_run_in_progress === true;
};

/**
 * User-facing copy while connector workflows finish their first sync.
 *
 * @example
 * // Input
 * buildWorkflowSyncingEmptyMessage([{ name: "gmail" }]);
 * // Output
 * // "Syncing gmail action items. They will appear here when the first run finishes."
 */
/** Consecutive unchanged digest-item polls before treating a stuck RUNNING transform as done. */
export const STABLE_DIGEST_POLLS_FOR_COMPLETE = 2;

export const FIRST_RUN_STABLE_WORKFLOWS_KEY = "actionItemsFirstRunStableWorkflowIds";

/**
 * Workflow ids whose first digest output has already stabilized this session.
 *
 * @example
 * readFirstRunStableWorkflowIds();
 * // Set { "41796610-6883-4d25-bb74-bc11c7e4edb0" }
 */
export const readFirstRunStableWorkflowIds = () => {
  try {
    let raw = localStorage.getItem(FIRST_RUN_STABLE_WORKFLOWS_KEY);
    if (!raw) {
      const sessionRaw = sessionStorage.getItem(FIRST_RUN_STABLE_WORKFLOWS_KEY);
      if (sessionRaw) {
        localStorage.setItem(FIRST_RUN_STABLE_WORKFLOWS_KEY, sessionRaw);
        sessionStorage.removeItem(FIRST_RUN_STABLE_WORKFLOWS_KEY);
        raw = sessionRaw;
      }
    }
    const parsed = JSON.parse(raw || "[]");
    return new Set(
      (Array.isArray(parsed) ? parsed : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );
  } catch {
    return new Set();
  }
};

/**
 * Remember that a workflow's first digest finished for the rest of the browser session.
 *
 * @example
 * persistFirstRunStableWorkflowId("41796610-6883-4d25-bb74-bc11c7e4edb0");
 */
export const persistFirstRunStableWorkflowId = (workflowId) => {
  const id = String(workflowId || "").trim();
  if (!id) return;
  const ids = readFirstRunStableWorkflowIds();
  if (ids.has(id)) return;
  ids.add(id);
  localStorage.setItem(FIRST_RUN_STABLE_WORKFLOWS_KEY, JSON.stringify([...ids]));
};

/**
 * True when a workflow is still in its initial setup sync and should show first-run UI.
 *
 * @example
 * // Input — completed workflow id already in stable set
 * isWorkflowInFirstRunSetup(
 *   { workflow_id: "wf-1", first_run_state: "transform_running" },
 *   new Set(["wf-1"])
 * );
 * // Output
 * // false
 */
export const isWorkflowInFirstRunSetup = (workflow, stableWorkflowIds = new Set()) => {
  const workflowId = String(workflow?.workflow_id || "").trim();
  if (workflowId && stableWorkflowIds.has(workflowId)) {
    return false;
  }
  return isWorkflowLikelyGeneratingFirstItems(workflow);
};

/**
 * True while a workflow is actively syncing action items (first run or scheduled run).
 *
 * @example
 * isWorkflowSyncingActionItems({ job_status: "RUNNING", last_synced_at: 1715000000000 });
 * // true
 */
export const isWorkflowSyncingActionItems = (workflow) => {
  if (!workflow || workflow.active === false) return false;
  const status = getWorkflowLifecycleStatus(workflow);
  // Stuck "still running" state must not keep polling and spinning forever.
  return status.showProgress && status.progressPercent < SYNC_STALE_PROGRESS_PERCENT;
};

const buildWorkflowSyncStatusIdleText = (workflow) => {
  const lastSyncedMs = parseWorkflowTimestampMs(workflow?.last_synced_at);
  if (lastSyncedMs > 0) {
    return {
      syncing: false,
      statusText: timestampFormatter(workflow.last_synced_at, TIMESTAMP_MINUTE_FORMAT),
    };
  }
  return { syncing: false, statusText: "Not synced yet" };
};

const buildWorkflowSyncStatusActiveText = (identifiedCount) => {
  if (identifiedCount > 0) {
    const countLabel = identifiedCount === 1 ? "1 identified" : `${identifiedCount} identified`;
    return { syncing: true, statusText: `Syncing · ${countLabel}` };
  }
  return { syncing: true, statusText: "Syncing" };
};

/**
 * Status line for a workflow sync notice (syncing label or last-synced timestamp).
 *
 * @example
 * buildWorkflowSyncStatusNoticeText({ last_synced_at: 1715000000000 });
 * // { syncing: false, statusText: "May 5, 2026 3:47 PM" }
 *
 * @example
 * buildWorkflowSyncStatusNoticeText(
 *   { first_run_in_progress: true, first_run_state: "TRANSFORM_RUNNING" },
 *   { identifiedCount: 3 }
 * );
 * // { syncing: true, statusText: "Syncing · 3 identified" }
 *
 * @example
 * buildWorkflowSyncStatusNoticeText(
 *   { first_run_state: "completed", last_synced_at: 1715000000000, job_status: "RUNNING" }
 * );
 * // { syncing: false, statusText: "May 5, 2026 3:47 PM" }
 */
export const buildWorkflowSyncStatusNoticeText = (workflow, options = {}) => {
  const sessionStableWorkflowIds = options.sessionStableWorkflowIds || new Set();
  const identifiedCount = Math.max(0, Number(options.identifiedCount) || 0);
  const workflowId = String(workflow?.workflow_id || "").trim();

  if (workflowId && sessionStableWorkflowIds.has(workflowId)) {
    return buildWorkflowSyncStatusIdleText(workflow);
  }

  if (isWorkflowFirstRunComplete(workflow)) {
    return buildWorkflowSyncStatusIdleText(workflow);
  }

  if (isWorkflowLikelyGeneratingFirstItems(workflow)) {
    return buildWorkflowSyncStatusActiveText(identifiedCount);
  }

  return buildWorkflowSyncStatusIdleText(workflow);
};

/**
 * True while the workflow sync status strip should be visible (actively syncing).
 *
 * @example
 * isWorkflowSyncStatusNoticeActive(
 *   { first_run_state: "transform_running", first_run_in_progress: true },
 *   { identifiedCount: 2 }
 * );
 * // true
 */
export const isWorkflowSyncStatusNoticeActive = (workflow, options = {}) =>
  buildWorkflowSyncStatusNoticeText(workflow, options).syncing;

export const buildWorkflowSyncingEmptyMessage = (workflows) => {
  const names = (Array.isArray(workflows) ? workflows : [])
    .map((workflow) => String(workflow?.name || "").trim())
    .filter(Boolean);
  if (names.length === 1) {
    return `Syncing ${names[0]} action items. They will appear here when the first run finishes.`;
  }
  if (names.length > 1) {
    return `Syncing action items for ${names.join(", ")}. They will appear here when the first run finishes.`;
  }
  return "Syncing action items from your connected app. They will appear here when the first run finishes.";
};
